import clientFetcher from '@lib/client-fetcher';
import type { ConnectedUser, School, UserStatus } from 'types/collections';

/**
 * Service for retrieving school data based on user role
 */
export class SchoolService {
  async getSchoolProfile(): Promise<School | null> {
    return clientFetcher<School>({
      url: '/api/schools/profile',
    });
  }

  /**
   * Get connected volunteers for the school
   */
  async getConnectedVolunteers(): Promise<{
    connectedUsers: ConnectedUser[];
    totalCount: number;
  }> {
    return clientFetcher<{ connectedUsers: ConnectedUser[]; totalCount: number }>({
      url: '/api/schools/connected-volunteers',
    });
  }

  /**
   * Update school profile information
   */
  async updateProfile(updates: Partial<School>): Promise<void> {
    await clientFetcher<void>({
      url: '/api/schools/profile',
      method: 'PATCH',
      body: updates,
    });
  }

  /**
   * Request to delete school profile
   */
  async requestAccountDeletion(): Promise<void> {
    return await clientFetcher({
      url: '/api/schools/profile-deletion',
      method: 'POST',
    });
  }

  // Admin functions using API routes

  /**
   * Get all schools for admin management view
   */
  async getAllSchoolsForAdmin(): Promise<School[]> {
    return clientFetcher<School[]>({
      url: '/api/admin/schools',
    });
  }

  /**
   * Get single school by ID for admin view with full details
   */
  async getSchoolByIdForAdmin(schoolId: string): Promise<School> {
    return clientFetcher<School>({
      url: `/api/admin/schools/${schoolId}`,
    });
  }

  /**
   * Update school status for admin (only status can be changed)
   */
  async updateSchoolForAdmin(
    schoolId: string,
    updates: {
      status: UserStatus;
    },
  ): Promise<void> {
    await clientFetcher<{ success: boolean; message: string; data: School }>({
      url: `/api/admin/schools/${schoolId}`,
      method: 'PATCH',
      body: updates,
    });
  }
}
